<template>
  <div class="seminar-assignment-inning-fi">
    <div class="seminar-assignment-inning-fi__head">
      <div class="status-label">
        <div :class="['inning-status', `inning-status--${inningStatus}`]">
          <div class="inning-status__tooltip">
            <div :class="['inning-status', `inning-status--${inningStatus}`]" />
            {{ inningStatusText }}
          </div>
        </div>

        <span v-if="fields.inningCounterLabel?.value">
          {{ fields.inningCounterLabel.value.replace('{0}', String(idx + 1)) }}
        </span>
      </div>

      <div class="date">
        <div class="seminar-assignment-inning-fi__icon-value">
          <icon-fi
            icon="calendar"
            class="seminar-assignment-inning-fi__icon-value__icon"
          />
          <span
            v-if="inning.start.day"
            class="seminar-assignment-inning-fi__icon-value__value"
          >
            {{ inning.start.day }} -
            {{ inning.end.day }}
          </span>
        </div>

        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="inningDeadlineLabel"
          class="date__deadline"
          v-html="inningDeadlineLabel"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <div class="price">
        <div class="seminar-assignment-inning-fi__icon-value">
          <icon-fi
            icon="money"
            class="seminar-assignment-inning-fi__icon-value__icon"
          />
          <span class="seminar-assignment-inning-fi__icon-value__value">{{ inning.price }}</span>
        </div>
      </div>

      <div class="action">
        <button-fi
          class="button-fi__primary"
          data-test="register-seminar"
          :target="hasExternalDescription ? '_blank' : null"
          :href="hasExternalDescription ? inning.externalDescription : null"
          :natural-events="hasExternalDescription"
          @click="hasExternalDescription ? null : $emit('register')"
        >
          {{ fields.registerSeminar?.value }}
        </button-fi>
      </div>

      <div
        class="toggle"
        @click="showEventBody = !showEventBody"
      >
        <div :class="{ 'toggle-details': true, 'toggle-details--active': showEventBody }">
          <icon-fi
            icon="chevron-down"
            class="toggle-details__icon"
          />
          {{ fields.toggleDetails?.value }}
        </div>
      </div>
    </div>

    <div
      :class="{
        'seminar-assignment-inning-fi__body': true,
        'seminar-assignment-inning-fi__body--expanded': showEventBody,
      }"
    >
      <div class="seminar-assignment-inning-fi__body__inner">
        <div class="seminar-assignment-inning-fi__details">
          <div
            v-for="seminar in inning.seminars"
            :key="seminar.id"
            class="seminar-assignment-inning-fi__inning"
          >
            <div class="seminar-assignment-inning-fi__inning__title">
              {{ seminar.title }}
            </div>
            <template
              v-for="event in seminar.events"
              :key="event.id"
            >
              <template v-if="event.followEvents?.length">
                <div
                  v-for="eventDay in event.followEvents"
                  :key="eventDay.id"
                  class="seminar-assignment-inning-fi__inning__event"
                >
                  <template v-if="eventDay.start">
                    <div class="seminar-assignment-inning-fi__icon-value">
                      <icon-fi
                        icon="calendar"
                        class="seminar-assignment-inning-fi__icon-value__icon"
                      />
                      <span class="seminar-assignment-inning-fi__icon-value__value">
                        {{ eventDay.start.day }}
                      </span>
                    </div>
                    <div class="seminar-assignment-inning-fi__icon-value">
                      <icon-fi
                        icon="clock"
                        class="seminar-assignment-inning-fi__icon-value__icon"
                      />
                      <span class="seminar-assignment-inning-fi__icon-value__value">
                        {{ eventDay.start.time }} - {{ eventDay.end.time }}
                      </span>
                    </div>
                  </template>

                  <div class="location">
                    <div class="seminar-assignment-inning-fi__icon-value location__info">
                      <icon-fi
                        icon="map-pin"
                        class="seminar-assignment-inning-fi__icon-value__icon"
                      />
                      <span class="seminar-assignment-inning-fi__icon-value__value">
                        {{
                          event.location.type === 'onsite'
                            ? event.location.city
                            : $dict.get('seminars.Location.Online')
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="seminar-assignment-inning-fi__inning__event">
                  <div class="seminar-assignment-inning-fi__icon-value">
                    <icon-fi
                      icon="calendar"
                      class="seminar-assignment-inning-fi__icon-value__icon"
                    />
                    <span class="seminar-assignment-inning-fi__icon-value__value">
                      {{ event.start.day }}
                    </span>
                  </div>
                  <div class="seminar-assignment-inning-fi__icon-value">
                    <icon-fi
                      icon="clock"
                      class="seminar-assignment-inning-fi__icon-value__icon"
                    />
                    <span class="seminar-assignment-inning-fi__icon-value__value">
                      {{ event.start.time }} - {{ event.end.time }}
                    </span>
                  </div>

                  <div class="location">
                    <div class="seminar-assignment-inning-fi__icon-value location__info">
                      <icon-fi
                        icon="map-pin"
                        class="seminar-assignment-inning-fi__icon-value__icon"
                      />
                      <span class="seminar-assignment-inning-fi__icon-value__value">
                        {{
                          event.location.type === 'onsite'
                            ? event.location.city
                            : $dict.get('seminars.Location.Online')
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ButtonFi, IconFi } from 'atoms';
import { computed, ref } from 'vue';

import { useFields } from '@/composables/fields';

defineOptions({
  name: 'SeminarAssignmentInningFi',
});

const fields = useFields('SeminarsOverviewFiFields');

interface Props {
  inning?: any;
  idx?: number;
}

const props = withDefaults(defineProps<Props>(), {
  inning: null,
  idx: 0,
});

defineEmits<{
  register: [value: void];
}>();

const showEventBody = ref<boolean>(false);

const inningDeadlineLabel = computed(() => {
  if (fields.seminarDeadlineLabel?.value && props.inning?.deadline) {
    return fields.seminarDeadlineLabel.value.replace(
      '{0}',
      `<span class="fi-semibold">${props.inning.deadline}</span>`,
    );
  }
  return '';
});

const inningStatus = computed(() => {
  if (props.inning.actualParticipants === props.inning.maxParticipants) {
    return 'full';
  }

  if ((100 * props.inning.actualParticipants) / props.inning.maxParticipants > 25) {
    return 'partly';
  }

  return 'free';
});

const inningStatusText = computed(() => {
  if (inningStatus.value === 'full') {
    return fields.seminarStatusFull?.value;
  }

  if (inningStatus.value === 'partly') {
    return fields.seminarStatusPartly?.value;
  }

  return fields.seminarStatusFree?.value;
});

const hasExternalDescription = computed(() => Boolean(props.inning?.externalDescription));
</script>

<style scoped src="./seminar-assignment-inning-fi" />
